// LoadData の適用順序だけをまとめる orchestrator です。
// 実際の画面固有処理は呼び出し元から受け取り、ここでは実行順の管理に専念します。

export function createLoadDataOrchestrator(deps) {
    function isLatestRequestCode(code) {
        if (typeof deps.isLatestRequest !== "function") return true;
        return deps.isLatestRequest(code);
    }

    function skipStaleRequest(payload) {
        if (typeof deps.onSkippedStaleRequest === "function") {
            deps.onSkippedStaleRequest(payload);
        }
    }

    function measureStep(timing, name, action) {
        const startedAt = deps.now();
        const result = action();
        timing.stepMs[name] = Math.round(deps.now() - startedAt);
        return result;
    }

    return {
        // GetEntryData のレスポンスを Entry Form へ反映します。
        applyResponseData({ code, data, timing, currentKakouType }) {
            if (!isLatestRequestCode(code)) {
                skipStaleRequest({ code, timing, phase: "beforePrepare" });
                return;
            }

            timing.responseAt = deps.now();
            timing.applyStartAt = timing.responseAt;

            measureStep(timing, "prepareEntry", () => {
                deps.prepareEntry(data);
            });
            deps.afterPrepareEntry({ code });

            deps.deferApply(() => {
                if (!isLatestRequestCode(code)) {
                    skipStaleRequest({ code, timing, phase: "beforeDeferredApply" });
                    return;
                }

                try {
                    const detailData = measureStep(timing, "deepCopyDetail", () => deps.deepCopyDetail(data));
                    measureStep(timing, "storeDetailCopy", () => {
                        deps.storeDetailCopy(detailData);
                    });
                    deps.afterDetailReceived({ code, detailData });

                    measureStep(timing, "ensureFukushizaiDetailRows", () => {
                        deps.ensureFukushizaiDetailRows();
                    });

                    const effectiveDetailData = deps.getEffectiveDetailData();
                    deps.afterDetailPrepared({ code, detailData: effectiveDetailData });

                    const initialFukushizaiContext = deps.resolveInitialFukushizaiContext(effectiveDetailData);

                    measureStep(timing, "mergeEntryProjection", () => {
                        deps.mergeEntryProjection(effectiveDetailData, initialFukushizaiContext);
                    });
                    deps.afterMergeEntryProjection({ code, initialFukushizaiContext });

                    measureStep(timing, "applyEntryUiState", () => {
                        deps.applyEntryUiState(data);
                    });
                    deps.afterApplyEntryUiState({ code });

                    measureStep(timing, "syncGenka", () => {
                        deps.syncGenka(effectiveDetailData, data);
                    });
                    deps.afterSyncGenka({ code });

                    measureStep(timing, "dispatchBaseEntryData", () => {
                        deps.dispatchBaseEntryData(code);
                    });
                    deps.afterDispatchBaseEntryData({ code });

                    measureStep(timing, "applyDetailRowsToGrids", () => {
                        deps.applyDetailRowsToGrids(effectiveDetailData);
                    });
                    deps.afterApplyDetailRows({ code, detailData: effectiveDetailData });

                    measureStep(timing, "hydrateMasterNamesKickoff", () => {
                        if (typeof deps.hydrateMasterNamesKickoff === "function") {
                            deps.hydrateMasterNamesKickoff(code, effectiveDetailData, data);
                        }
                    });

                    measureStep(timing, "applyTotalRowsByPanelDefinition", () => {
                        deps.applyTotalRowsByPanelDefinition(effectiveDetailData);
                    });
                    measureStep(timing, "applyTotalRowsBySectionDefinition", () => {
                        deps.applyTotalRowsBySectionDefinition(effectiveDetailData);
                    });
                    measureStep(timing, "applyPlRows", () => {
                        deps.applyPlRows(data.data);
                    });

                    const nextKakouType = deps.resolveInitialDemandKakouType(effectiveDetailData);
                    measureStep(timing, "scheduleVisibleGridRefresh", () => {
                        deps.scheduleVisibleGridRefresh(nextKakouType === "" ? currentKakouType : nextKakouType, nextKakouType);
                    });
                    measureStep(timing, "loadColorwayMatricesKickoff", () => {
                        deps.loadColorwayMatricesKickoff(code);
                    });
                    measureStep(timing, "finalizeEntryVisualDataKickoff", () => {
                        deps.finalizeEntryVisualDataKickoff(code, effectiveDetailData, data);
                    });

                    timing.applyEndAt = deps.now();
                    deps.onApplyCompleted({
                        code,
                        timing
                    });
                } catch (error) {
                    deps.onApplyError(error, {
                        code,
                        timing,
                        phase: "deferredApply"
                    });
                }
            });
        }
    };
}
